import {
  Badge,
  Cushion,
  FlexContainer,
  Grid,
  Image,
  Rectangle,
  Typography,
} from '@pitchero/react-ui';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import Icon from '../icon';
import { FIXTURE_PROP_TYPE } from '../../lib/prop-types/fixture';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import ClubLink from '../club-link';
import { Trans, withTranslation } from '../../lib/i18n';

const MatchDetails = ({ match, club, type, isLast, isMobile }) => {
  // Renders the row for each side, with logo/badge, team name
  const renderSideRow = ({ side, opponent }) => (
    <Grid columns="36px 1fr" columnGap="10px" alignItems="center">
      {club.logo && side.name !== opponent ? (
        <Image src={club.logo} alt={club.name} height={36} width={36} transformation="square" />
      ) : (
        <Badge primary={side.primaryColour} secondary={side.secondaryColour} size="36" />
      )}
      <Typography preset="document" size={14}>
        {side.name === opponent ? opponent : match.teamName}
      </Typography>
    </Grid>
  );

  const getMatchStatusOrScore = (score) => {
    if (match.status === 'Cancelled') {
      return 'C';
    }

    if (match.status === 'Postponed') {
      return 'P';
    }

    return score;
  };

  // Renders the black score box for a result
  const renderScore = (score) => {
    if (!match.isCancelledOrPostponed && !score) return null;
    return (
      <Rectangle
        fill="#000"
        radius="rounded"
        style={{ maxHeight: '44px', boxSizing: 'border-box' }}
      >
        <Cushion vertical="10px" horizontal="18px">
          <FlexContainer justifyContent="center" alignItems="center" component="div">
            <Typography
              preset="title"
              color="white"
              size={20}
              style={{
                whiteSpace: 'nowrap',
              }}
            >
              {getMatchStatusOrScore(score)}
            </Typography>
          </FlexContainer>
        </Cushion>
      </Rectangle>
    );
  };

  const kickOff = (i18nKey, text) => (
    <Typography preset="subtitle--small" color="dustygrey" component="h4">
      <Trans i18nKey={`teams:${i18nKey}`}>{text}</Trans>
      &nbsp;{DateTime.fromISO(match.dateTime).setZone(club.timezone).toFormat('HH:mm')}
    </Typography>
  );

  return (
    <ClubLink
      route={`team.match_centre.${type === 'result' ? 'report' : 'overview'}`}
      params={{ teamId: match.teamId, fixtureId: match.id }}
    >
      <a>
        <FlexContainer
          flexDirection="column"
          style={{
            minHeight: isMobile ? 'none' : '225px',
            backgroundColor: '#fff',
            boxSizing: 'border-box',
            borderBottom: isLast ? 'none' : '1px solid #d9d9d9',
            padding: '16px',
          }}
          component="div"
        >
          <FlexContainer
            justifyContent="space-between"
            alignItems="center"
            style={{ paddingBottom: '20px', gap: '10px' }}
          >
            <Rectangle fill="#e8fde9" radius="rounded">
              <Cushion all="xsmall">
                <Typography color="#0d8211" size={14} weight={600} transform="uppercase">
                  {match.isCancelledOrPostponed
                    ? ` ${match.status} -  ${match.type}`
                    : `${match.division ? match.division : match.type}`}
                </Typography>
              </Cushion>
            </Rectangle>
            <Typography
              size={14}
              color="#666"
              style={{ whiteSpace: 'pre-line', wordBreak: 'keep-all' }}
              align="right"
            >
              {match.dateTime && kickOff(`kickoff_label_${club.sport}_shorthand`, 'KO')}
              {match.location && (
                <>
                  &thinsp;&middot;&thinsp;
                  {match.location}
                </>
              )}
            </Typography>
          </FlexContainer>
          <FlexContainer
            flexDirection="column"
            justifyContent="center"
            style={{ height: '100%', margin: 'auto 0' }}
          >
            <Grid columns="1fr auto" rowGap="small">
              <Grid
                rows="1fr 1fr"
                alignItems="center"
                alignContent="center"
                rowGap={isMobile ? '20px' : '30px'}
                style={{ paddingRight: '5px' }}
              >
                {renderSideRow({
                  side: match.homeSide,
                  opponent: match.opponent,
                })}
                {renderSideRow({
                  side: match.awaySide,
                  opponent: match.opponent,
                })}
              </Grid>
              <Grid columns="1fr 20px" alignItems="center" justifyContent="end" columnGap="5px">
                {type === 'fixture' && <Typography preset="body--small" color="#000" />}
                {type === 'result' && (
                  <Grid rows="1fr 1fr" rowGap={isMobile ? '20px' : '30px'} alignItems="center">
                    {renderScore(match.homeSide.score)}
                    {renderScore(match.awaySide.score)}
                  </Grid>
                )}
                <Icon name="arrow-right" color="gray" size={16} />
              </Grid>
            </Grid>
          </FlexContainer>
        </FlexContainer>
      </a>
    </ClubLink>
  );
};

MatchDetails.propTypes = {
  match: FIXTURE_PROP_TYPE.isRequired,
  club: CLUB_PROP_TYPES.isRequired,
  type: PropTypes.oneOf(['result', 'fixture']).isRequired,
  isLast: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
};

MatchDetails.defaultProps = {
  isLast: false,
};
export default withTranslation(['teams'])(MatchDetails);
